'use client'

/**
 * lib/hooks/useClientTimezone.ts
 * SSR-safe hook that detects and persists the client's preferred timezone.
 *
 * - Server: returns 'America/Santiago' (safe static export default)
 * - Browser: reads localStorage('apex.userTz'), falls back to Intl detection
 * - setTz persists the chosen value to localStorage
 */

import { useState, useCallback } from 'react'

const STORAGE_KEY = 'apex.userTz'
const DEFAULT_TZ = 'America/Santiago'

function detectTz(): string {
  if (typeof window === 'undefined') return DEFAULT_TZ
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) return stored
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ
  } catch {
    return DEFAULT_TZ
  }
}

export function useClientTimezone(): [string, (tz: string) => void] {
  const [tz, setTzState] = useState<string>(() => detectTz())

  const setTz = useCallback((newTz: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, newTz)
    } catch {
      // localStorage unavailable (private browsing, etc.) — ignore
    }
    setTzState(newTz)
  }, [])

  return [tz, setTz]
}
